import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import {
  PlannedInfrastructureItem,
  InfrastructureCategory,
  InfrastructureLifecycle,
  MAX_INFRASTRUCTURE_ITEMS_PER_DRAFT,
} from '../../../../types/clientOnboarding';

interface Props {
  items: PlannedInfrastructureItem[];
  onChange: (items: PlannedInfrastructureItem[]) => void;
  errors: Record<string, string>;
}

const CATEGORIES: InfrastructureCategory[] = [
  'water_source', 'pump', 'filter', 'fertigation_unit', 'collector',
  'valve', 'flow_meter', 'pressure_sensor', 'gateway',
  'valve_node', 'sensor_node', 'relay_node', 'adc_node', 'ct_monitor',
];

const LIFECYCLES: InfrastructureLifecycle[] = ['PLANNED', 'AWAITING_HARDWARE', 'UNASSIGNED'];

const inputCls =
  'w-full p-2 border border-[var(--border-light)] rounded-lg bg-[var(--surface)] text-[var(--text-primary)] text-sm focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent';
const labelCls = 'block text-xs font-medium text-[var(--text-secondary)] mb-1';

const InfrastructureStep: React.FC<Props> = ({ items, onChange, errors }) => {
  const { t } = useTranslation(['admin_client_onboarding']);

  // Static key maps — avoids dynamic template literal keys
  const categoryLabels: Record<InfrastructureCategory, string> = {
    water_source: t('step6.categories.water_source'),
    pump: t('step6.categories.pump'),
    filter: t('step6.categories.filter'),
    fertigation_unit: t('step6.categories.fertigation_unit'),
    collector: t('step6.categories.collector'),
    valve: t('step6.categories.valve'),
    flow_meter: t('step6.categories.flow_meter'),
    pressure_sensor: t('step6.categories.pressure_sensor'),
    gateway: t('step6.categories.gateway'),
    valve_node: t('step6.categories.valve_node'),
    sensor_node: t('step6.categories.sensor_node'),
    relay_node: t('step6.categories.relay_node'),
    adc_node: t('step6.categories.adc_node'),
    ct_monitor: t('step6.categories.ct_monitor'),
  };

  const lifecycleLabels: Record<InfrastructureLifecycle, string> = {
    PLANNED: t('step6.lifecycle.PLANNED'),
    AWAITING_HARDWARE: t('step6.lifecycle.AWAITING_HARDWARE'),
    UNASSIGNED: t('step6.lifecycle.UNASSIGNED'),
  };

  const addItem = () => {
    const next: PlannedInfrastructureItem = {
      id: `infra-${Date.now()}`,
      category: 'valve',
      label: '',
      lifecycle: 'PLANNED',
      quantity: 1,
    };
    onChange([...items, next]);
  };

  const removeItem = (id: string) => {
    onChange(items.filter(i => i.id !== id));
  };

  const updateItem = (id: string, patch: Partial<PlannedInfrastructureItem>) => {
    onChange(items.map(i => (i.id === id ? { ...i, ...patch } : i)));
  };

  const hasPump = items.some(i => i.category === 'pump');

  return (
    <div className="space-y-4" data-testid="onboarding-step-infra">
      <div>
        <h3 className="text-heading-3 text-[var(--text-primary)] mb-1">{t('step6.heading')}</h3>
        <p className="text-sm text-[var(--text-secondary)]">{t('step6.description')}</p>
      </div>

      {items.length === 0 && (
        <p className="text-sm text-[var(--text-tertiary)] italic">{t('step8.noDataHint')}</p>
      )}

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="border border-[var(--border-light)] rounded-lg p-3 space-y-2" data-testid={`infra-item-${item.id}`}>
            <div className="flex items-center gap-2">
              <div>
                <label htmlFor={`infra-category-${item.id}`} className="sr-only">{t('step6.categoriesLabel')}</label>
                <select id={`infra-category-${item.id}`} className={inputCls} value={item.category}
                  onChange={e => updateItem(item.id, { category: e.target.value as InfrastructureCategory })}
                  data-testid={`infra-category-${item.id}`}>
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>{categoryLabels[c]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={`infra-lifecycle-${item.id}`} className="sr-only">{t('step6.lifecycleLabel')}</label>
                <select id={`infra-lifecycle-${item.id}`} className={inputCls} value={item.lifecycle}
                  onChange={e => updateItem(item.id, { lifecycle: e.target.value as InfrastructureLifecycle })}
                  data-testid={`infra-lifecycle-${item.id}`}>
                  {LIFECYCLES.map(lc => (
                    <option key={lc} value={lc}>{lifecycleLabels[lc]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={`infra-qty-${item.id}`} className="sr-only">{t('step6.quantity')}</label>
                <input id={`infra-qty-${item.id}`} className={`${inputCls} w-20`} type="number" min={1} value={item.quantity}
                  onChange={e => updateItem(item.id, { quantity: Number(e.target.value) })}
                  aria-describedby={errors[`infra.${item.id}.quantity`] ? `infra-qty-err-${item.id}` : undefined}
                  data-testid={`infra-qty-${item.id}`} />
              </div>
              <button onClick={() => removeItem(item.id)}
                className="p-2 text-[var(--text-tertiary)] hover:text-[var(--color-danger)] rounded"
                aria-label={t('step6.removeItemTarget', { index: items.indexOf(item) + 1, name: item.label || categoryLabels[item.category] })}
                data-testid={`infra-remove-${item.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {errors[`infra.${item.id}.quantity`] && (
              <p id={`infra-qty-err-${item.id}`} className="text-[var(--color-danger)] text-xs mt-1" role="alert">{errors[`infra.${item.id}.quantity`]}</p>
            )}
            <div>
              <label htmlFor={`infra-label-${item.id}`} className="sr-only">{t('step6.label')}</label>
              <input id={`infra-label-${item.id}`} className={inputCls} value={item.label}
                onChange={e => updateItem(item.id, { label: e.target.value })}
                placeholder={t('step6.label_ph')}
                aria-invalid={!!errors[`infra.${item.id}.label`]}
                aria-describedby={errors[`infra.${item.id}.label`] ? `infra-label-err-${item.id}` : undefined}
                data-testid={`infra-label-${item.id}`} />
              {errors[`infra.${item.id}.label`] && (
                <p id={`infra-label-err-${item.id}`} className="text-[var(--color-danger)] text-xs mt-1" role="alert">{errors[`infra.${item.id}.label`]}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {items.length < MAX_INFRASTRUCTURE_ITEMS_PER_DRAFT ? (
        <button onClick={addItem}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-dashed border-[var(--border-light)] rounded-lg text-[var(--text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors"
          data-testid="onboarding-add-infra">
          <Plus className="w-4 h-4" /> {t('step6.addItem')}
        </button>
      ) : (
        <button disabled
          className="flex items-center gap-2 px-4 py-2 text-sm border border-dashed border-[var(--border-light)] rounded-lg text-[var(--text-tertiary)] cursor-not-allowed opacity-50"
          data-testid="onboarding-add-infra-disabled"
          title={t('validation.tooManyItems', { max: MAX_INFRASTRUCTURE_ITEMS_PER_DRAFT })}>
          <Plus className="w-4 h-4" /> {t('step6.addItem')}
        </button>
      )}

      {hasPump && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="onboarding-pump-safety-note">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">{t('step6.pumpSafetyTitle')}</p>
            <p>{t('step6.pumpSafety')}</p>
          </div>
        </div>
      )}

      <p className="text-xs text-[var(--text-tertiary)]">{t('step6.lifecycleNote')}</p>
    </div>
  );
};

export default InfrastructureStep;
